import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Heart, Leaf, Users, Phone, Mail, MapPin, Calendar, ArrowRight, Sparkles, Shield, BookOpen, MessageCircle, Brain, Target, Bed, Apple } from 'lucide-react';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import heroImage from '@/assets/hero_image.jpg';

const IVFSupportPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Optimized SEO Meta Tags
    document.title = "IVF Support & Fertility Care | Currimundi Clinic";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Gentle IVF support with Chinese Medicine in Currimundi. Acupuncture, herbs, and emotional care for your fertility journey on the Sunshine Coast.");
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "IVF Support & Fertility Care | Currimundi Clinic");
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Gentle IVF support with Chinese Medicine in Currimundi. Acupuncture, herbs, and emotional care for your fertility journey on the Sunshine Coast.");
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/ivf-support");
    }
    
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/ivf-support-chinese-medicine.jpg");
    }
    
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "IVF Support & Fertility Care | Currimundi Clinic");
    }

    // Enhanced LocalBusiness Schema
    const ivfSchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - IVF Support & Fertility Care",
      "image": "https://womenandtheway.com.au/ivf-support-chinese-medicine.jpg",
      "description": "Gentle IVF support with Chinese Medicine in Currimundi. Acupuncture, herbs, and emotional care for your fertility journey on the Sunshine Coast.",
      "url": "https://womenandtheway.com.au/ivf-support",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "IVF Support, Fertility Care, Acupuncture, Chinese Medicine, Fertility Treatment",
      "areaServed": "Sunshine Coast, Currimundi, Caloundra, Maroochydore",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ivfSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  IVF Support & Fertility Care
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Gentle support for your IVF journey. We're here to nurture your body, mind, and spirit every step of the way.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  IVF is a complex journey that requires physical, emotional, and spiritual support. At Women and the Way, we provide comprehensive care throughout your IVF cycle to optimize your chances of success and support your wellbeing.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're preparing for your first cycle, managing side effects, or supporting your body between cycles, our treatments help optimize your reproductive health and reduce stress. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of IVF protocols. Many of our clients also benefit from our <a href="/fertility-support" className="text-sage-600 hover:text-sage-800 underline transition-colors">fertility support</a> and <a href="/emotional-healing" className="text-sage-600 hover:text-sage-800 underline transition-colors">emotional healing services</a>.
                </p>
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : 'Book Now'}</span>
                  <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={heroImage} 
                  alt="IVF support and fertility care consultation with Chinese Medicine in Currimundi clinic" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why IVF Support Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Heart className="w-8 h-8 text-sage-600" />
                Why IVF Support Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                IVF is a journey that asks so much of your body, mind, and heart. Chinese medicine offers gentle, evidence-based support to help you feel stronger, more balanced, and more connected to your body throughout this process.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Research shows that acupuncture during IVF can improve success rates, reduce stress, and support better egg quality and uterine lining. We offer a safe space to be held, heard, and supported during one of life's most vulnerable journeys.
              </p>
            </div>
          </div>
        </section>

        {/* What We Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Support
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {/* Cards for each support area */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">IVF Protocol Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Acupuncture before and after embryo transfer, stress reduction, and hormonal balance</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Stress & Anxiety Management</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Nervous system regulation, emotional support, and mental clarity</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Target className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Egg Quality & Uterine Lining</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Blood flow optimization, hormonal regulation, and reproductive health</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Bed className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Sleep & Recovery</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Restorative sleep support and post-procedure recovery</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Apple className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Digestive Health</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Gut health optimization and medication side effect support</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Immune Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Immune system balance and overall wellness during treatment</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                IVF Support In-Clinic or Online
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* In-Clinic Support */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Pre & Post Transfer Acupuncture</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Evidence-based protocols to support embryo implantation and reduce stress</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Chinese Herbal Medicine</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Custom formulas to support hormonal balance and reproductive health</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Stress Reduction Techniques</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Breathwork, meditation guidance, and nervous system regulation</p>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Lifestyle & Nutrition Guidance</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Evidence-based recommendations for optimal fertility support</p>
                </CardContent>
              </Card>
              {/* Online Sessions */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageCircle className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Telehealth Consultations</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Emotional support and guidance throughout your IVF process</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Herbal Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Custom formulas mailed to your door for ongoing support</p>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Self-Care Resources</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Acupressure guides, meditation recordings, and stress management tools</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* IVF Support Timeline */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                IVF Support Timeline
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {/* Timeline cards */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sage-600 font-bold">1</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Pre-IVF Preparation</h3>
                  <p className="text-sage-700">3-6 months of support to optimize egg quality, hormonal balance, and overall health</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sage-600 font-bold">2</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">During Stimulation</h3>
                  <p className="text-sage-700">Weekly sessions to support medication response, reduce side effects, and manage stress</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sage-600 font-bold">3</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Transfer Support</h3>
                  <p className="text-sage-700">Acupuncture before and after embryo transfer to optimize implantation</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sage-600 font-bold">4</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Two-Week Wait</h3>
                  <p className="text-sage-700">Gentle support during the waiting period, focusing on calm and connection</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What Makes Our IVF Support Unique */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What Makes Our IVF Support Unique
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <Heart className="w-12 h-12 text-sage-600 mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Evidence-Based Protocols</h3>
                  <p className="text-sage-700">We follow research-backed acupuncture protocols specifically designed for IVF support</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <Users className="w-12 h-12 text-sage-600 mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Emotional Safety</h3>
                  <p className="text-sage-700">A safe space to process the complex emotions that come with fertility treatment</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <Leaf className="w-12 h-12 text-sage-600 mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Holistic Approach</h3>
                  <p className="text-sage-700">We support your whole being - physical, emotional, and spiritual - throughout your journey</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <BookOpen className="w-12 h-12 text-sage-600 mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Ongoing Education</h3>
                  <p className="text-sage-700">We help you understand your body and treatment process, empowering you with knowledge</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Our IVF Support Toolkit */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                Our IVF Support Toolkit
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4 mx-auto" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Fertility Acupuncture Protocols</h3>
                  <p className="text-sage-700">Evidence-based point combinations for IVF support</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4 mx-auto" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Custom Herbal Formulas</h3>
                  <p className="text-sage-700">Tailored to your specific needs and treatment phase</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4 mx-auto" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Stress Management Tools</h3>
                  <p className="text-sage-700">Breathwork, meditation, and nervous system regulation</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4 mx-auto" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Nutrition Guidance</h3>
                  <p className="text-sage-700">Fertility-supporting foods and lifestyle recommendations</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4 mx-auto" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Emotional Support</h3>
                  <p className="text-sage-700">Safe space for processing the emotional journey of IVF</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4 mx-auto" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Partner Resources</h3>
                  <p className="text-sage-700">Support for partners navigating the IVF journey together</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Booking CTA */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6">
              Book Your IVF Support Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              You don't have to navigate this journey alone. Let us support you with gentle, evidence-based care that honours your courage and strength.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? 'Booking...' : 'Book Your Session'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default IVFSupportPage;
